import Quill, { type Delta } from "quill";
import { Formik } from "formik";
import { useMemo, useRef, useState } from "react";
import { Button } from "@repo/ui/components/Base/Button";
import { RichTextEditor } from "@repo/ui/components/Base/RichTextEditor";
import { meetingMinutesEditorOptions } from "./MeetingMinutesEditorForm";
import { Spinner } from "@repo/ui/components/Base/Spinner";
import { DialogBody, DialogFooter } from "@repo/ui/components/Base/Dialog";
import { Input } from "@repo/ui/components/Base/Input";
import { ConfirmDialog } from "@repo/ui/components/Base/ConfirmDialog";
import type { AIMinutesTemplateDto } from "@/repositories/aiMinutesTemplateRepo";

export interface MeetingMinutesAITemplateFormProps {
  onCancel?: () => void;
  onDelete?: (aiMinutesTemplate: AIMinutesTemplateDto) => void | Promise<void>;
  onSubmit?: (name: string, data: Delta) => void | Promise<void>;
  showDelete?: boolean;
  aiMinutesTemplate?: AIMinutesTemplateDto;
  withinDialog?: boolean;
}

export const MeetingMinutesAITemplateForm = ({
  onCancel,
  onDelete,
  onSubmit,
  showDelete = false,
  aiMinutesTemplate,
  withinDialog,
}: MeetingMinutesAITemplateFormProps) => {
  const quillRef = useRef<Quill>();

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const delta = useMemo(() => {
    return aiMinutesTemplate ? JSON.parse(aiMinutesTemplate.data) : [];
  }, [aiMinutesTemplate]);

  if (!aiMinutesTemplate) {
    return (
      <div className="flex flex-col flex-grow justify-center items-center">
        <Spinner />
      </div>
    );
  }

  return (
    <Formik
      initialValues={aiMinutesTemplate}
      onSubmit={async (values) => {
        if (!quillRef.current) return;

        quillRef.current.disable();

        await onSubmit?.(values.name, quillRef.current.getContents());
      }}
    >
      {({ submitForm, isSubmitting, handleChange, values }) => {
        const footer = (
          <>
            <Button
              disabled={isSubmitting}
              variant="destructive"
              onClick={onCancel}
            >
              Cancel
            </Button>

            <div className="grow" />

            {showDelete && (
              <Button
                disabled={isSubmitting}
                variant="destructive"
                onClick={() => {
                  setConfirmDeleteOpen(true);
                }}
              >
                Delete
              </Button>
            )}

            <Button disabled={isSubmitting} onClick={submitForm}>
              Save
            </Button>
          </>
        );

        const form = (
          <div className="flex flex-col flex-grow gap-y-4">
            <Input
              disabled={isSubmitting}
              label="Title"
              id="name"
              value={values.name}
              onChange={handleChange}
            />
            <RichTextEditor
              className="mt-5 h-0 flex-grow flex flex-col"
              containerStyles="h-0 flex-grow"
              options={meetingMinutesEditorOptions}
              contents={delta}
              ref={quillRef}
            />
            {!withinDialog && (
              <div className="flex flex-row justify-between">{footer}</div>
            )}
          </div>
        );

        return (
          <>
            <ConfirmDialog
              open={confirmDeleteOpen}
              setOpen={setConfirmDeleteOpen}
              message={
                <>
                  Are you sure you want to delete the "{values.name}" template?
                </>
              }
              onSubmit={async () => {
                await onDelete?.(aiMinutesTemplate);
                setConfirmDeleteOpen(false);
              }}
            />
            {withinDialog ? (
              <>
                <DialogBody className="h-[70vh] flex flex-col">
                  {form}
                </DialogBody>
                <DialogFooter className="justify-between">
                  {footer}
                </DialogFooter>
              </>
            ) : (
              form
            )}
          </>
        );
      }}
    </Formik>
  );
};
